import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Circle, Ellipse, Group, Layer, Line, Rect, Stage, Star, Text } from 'react-konva';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

function DragonRaceArena({ participants, fullscreenMode = false, elapsedMs = 0, height = 280, ultraFluidMode = false }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(900);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const updateWidth = () => {
      if (!containerRef.current) return;
      setWidth(Math.max(320, Math.floor(containerRef.current.clientWidth || 900)));
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const stars = useMemo(
    () => Array.from({ length: ultraFluidMode ? 10 : 22 }, (_, index) => ({
      id: index,
      x: ((index * 43) % 97) / 100,
      y: ((index * 29) % 83) / 100,
      radius: 0.8 + (index % 3) * 0.7,
      opacity: 0.35 + (index % 4) * 0.1,
    })),
    [ultraFluidMode]
  );

  const topPadding = 26;
  const bottomPadding = 26;
  const leftPadding = 34;
  const rightPadding = 54;
  const laneHeight = Math.max(18, (height - topPadding - bottomPadding) / Math.max(1, participants.length));
  const dragonScale = fullscreenMode ? 1 : 0.92;

  const dragonEntries = participants.map((dragon, index) => {
    const laneIndex = Number.isInteger(dragon.laneIndex) ? dragon.laneIndex : index;
    const usableHeight = Math.max(1, height - topPadding - bottomPadding);
    const laneY = topPadding + (laneIndex + 0.5) * (usableHeight / Math.max(1, participants.length));
    const wave = Math.sin((elapsedMs / 520) + laneIndex * 0.85) * Math.min(10, laneHeight * 0.22);
    const x = leftPadding + ((width - leftPadding - rightPadding) * clamp(dragon.progress / 100, 0, 1));
    const y = laneY + wave;

    return {
      ...dragon,
      x: clamp(x, leftPadding, width - rightPadding),
      y,
      rank: Number.isInteger(dragon.rank) ? dragon.rank : index + 1,
      glowRadius: dragon.boostActive ? 26 : 18,
      trailLength: dragon.boostActive ? 78 : 56,
      tokenWidth: (fullscreenMode ? 88 : 78) * dragonScale,
      tokenHeight: (fullscreenMode ? 34 : 30) * dragonScale,
    };
  });

  return (
    <div ref={containerRef} className="w-full">
      <Stage width={width} height={height} pixelRatio={1} listening={false}>
        <Layer listening={false}>
          <Rect
            x={0}
            y={0}
            width={width}
            height={height}
            cornerRadius={16}
            fillLinearGradientStartPoint={{ x: 0, y: 0 }}
            fillLinearGradientEndPoint={{ x: width, y: height }}
            fillLinearGradientColorStops={[0, '#020617', 0.45, '#111827', 1, '#1e293b']}
          />

          {stars.map((star) => (
            <Star
              key={star.id}
              x={star.x * width}
              y={star.y * height}
              numPoints={4}
              innerRadius={star.radius}
              outerRadius={star.radius * 2}
              fill={`rgba(255,255,255,${star.opacity})`}
              rotation={ultraFluidMode ? star.id * 17 : (star.id * 17 + elapsedMs / 40) % 360}
            />
          ))}

          {participants.map((_, index) => {
            const laneY = topPadding + (index + 0.5) * ((height - topPadding - bottomPadding) / Math.max(1, participants.length));
            return (
              <Group key={`lane-${index}`}>
                <Line
                  points={[leftPadding, laneY, width - rightPadding, laneY]}
                  stroke="rgba(148,163,184,0.18)"
                  strokeWidth={Math.max(1, laneHeight * 0.16)}
                  dash={ultraFluidMode ? undefined : [10, 8]}
                />
              </Group>
            );
          })}

          <Rect x={leftPadding - 8} y={12} width={6} height={height - 24} fill="rgba(34,211,238,0.75)" cornerRadius={999} />
          <Rect x={width - rightPadding + 16} y={12} width={6} height={height - 24} fill="rgba(255,255,255,0.9)" cornerRadius={999} />

          {Array.from({ length: 12 }).map((_, index) => (
            <Rect
              key={`finish-${index}`}
              x={width - rightPadding + 8}
              y={12 + index * ((height - 24) / 12)}
              width={14}
              height={(height - 24) / 12}
              fill={index % 2 === 0 ? 'rgba(255,255,255,0.95)' : 'rgba(15,23,42,0.95)'}
            />
          ))}

          {dragonEntries.map((dragon) => (
            <Group key={dragon.id} x={dragon.x} y={dragon.y}>
              <Line
                points={[-dragon.trailLength, 0, -18, 0]}
                stroke={dragon.color}
                strokeWidth={dragon.boostActive ? 10 : 7}
                opacity={dragon.boostActive ? 0.85 : 0.48}
                lineCap="round"
                shadowColor={dragon.color}
                shadowBlur={ultraFluidMode ? (dragon.boostActive ? 14 : 6) : (dragon.boostActive ? 20 : 10)}
              />

              {dragon.boostActive && (
                <Circle
                  x={0}
                  y={0}
                  radius={dragon.glowRadius}
                  fill={dragon.color}
                  opacity={0.14}
                  shadowColor={dragon.color}
                  shadowBlur={ultraFluidMode ? 16 : 24}
                />
              )}

              {dragon.isWinner && dragon.finished && (ultraFluidMode ? [0, 1] : [0, 1, 2]).map((ring) => {
                const t = ((elapsedMs / 1000 + ring * 0.32) % 0.95) / 0.95;
                return (
                  <Circle
                    key={`burst-${ring}`}
                    x={0}
                    y={0}
                    radius={18 + t * 72}
                    stroke={dragon.color}
                    strokeWidth={Math.max(0.5, (1 - t) * 4)}
                    opacity={(1 - t) * 0.65}
                  />
                );
              })}

              <Group>
                <Ellipse
                  x={0}
                  y={0}
                  radiusX={dragon.tokenWidth / 2}
                  radiusY={dragon.tokenHeight / 2}
                  fill={dragon.color}
                  stroke={dragon.isWinner ? '#fde68a' : dragon.finished ? '#bbf7d0' : 'rgba(255,255,255,0.7)'}
                  strokeWidth={dragon.isWinner ? 3 : dragon.finished ? 2.5 : 1.5}
                  shadowColor={dragon.isWinner ? '#fde68a' : dragon.color}
                  shadowBlur={ultraFluidMode ? (dragon.isWinner ? 18 : dragon.boostActive ? 12 : 8) : (dragon.isWinner ? 28 : dragon.boostActive ? 18 : 10)}
                  shadowOpacity={dragon.isWinner ? 0.9 : 0.55}
                />
                <Text
                  x={-dragon.tokenWidth / 2 + 8}
                  y={-11}
                  width={22}
                  height={22}
                  text="🐉"
                  fontSize={18}
                  align="center"
                  verticalAlign="middle"
                />
                <Text
                  x={-dragon.tokenWidth / 2 + 30}
                  y={-8}
                  width={dragon.tokenWidth - 38}
                  height={16}
                  text={dragon.name}
                  fontSize={11}
                  fontStyle="bold"
                  align="left"
                  verticalAlign="middle"
                  wrap="none"
                  ellipsis
                  fill="#f8fafc"
                />
              </Group>

              {dragon.isWinner && dragon.finished && (
                <Text
                  x={-10}
                  y={-dragon.tokenHeight / 2 - 22}
                  text="👑"
                  fontSize={18}
                />
              )}

              <Group x={-dragon.tokenWidth / 2 - 16} y={-12}>
                <Circle radius={11} fill="rgba(15,23,42,0.88)" stroke={dragon.color} strokeWidth={1.5} />
                <Text x={-7} y={-6.5} width={14} text={String(dragon.rank)} fontSize={10} fontStyle="bold" fill="#e2e8f0" align="center" />
              </Group>
            </Group>
          ))}

          <Text x={width - 72} y={10} text="Arrivée" fontSize={11} fill="#e2e8f0" />
        </Layer>
      </Stage>
    </div>
  );
}

export default DragonRaceArena;